import {
  LanguageNameSpaces,
  PrecisionConfig,
  ProcessedCoinExchangesData,
  SortingOrder,
} from "@shared/types";
import React, { useMemo } from "react";
import FlatList from "common/Lists/FlatList.tsx";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice.ts";
import CardSorters from "common/CardSorters/CardSorters.tsx";
import { ColumnType } from "@shared/common/CardsList/types.ts";
import useCardSorters from "@shared/common/CardSorters/useCardSorters.tsx";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general.ts";
import { RootState } from "app/store.ts";
import CoinAccountCard from "features/portfolio/CoinDrawer/CoinAccountCard.tsx";
import { EmptyStatesType } from "common/Icons/types.ts";
import EmptyView from "common/Empty/EmptyView.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

type ProcessedCoinExchangesDataWithKey = ProcessedCoinExchangesData & {
  key: string;
};

interface CoinAccountsCardsProps {
  data: ProcessedCoinExchangesDataWithKey[];
  renderTopContainer: () => React.JSX.Element;
  precisionSelector?: (state: RootState) => PrecisionConfig | {} | undefined;
}

const CoinAccountsCards = ({
  data,
  renderTopContainer,
  precisionSelector,
}: CoinAccountsCardsProps) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const displayCurrency = useSelector(selectDisplayCurrency);

  const columns: ColumnType<ProcessedCoinExchangesData>[] = useMemo(
    () => [
      {
        title: t("accounts.title"),
        span: COLUMN_SPANS[0],
        key: COLUMN_KEYS[0],
        sorter: (a, b) => sortByStringProperty(a, b, "client"),
      },
      {
        title: t("accounts.total"),
        span: COLUMN_SPANS[1],
        key: COLUMN_KEYS[1],
        sorter: (a, b) =>
          sortByNumberProperty(a, b, `total.${displayCurrency}`),
        defaultSortOrder: SortingOrder.DESCEND,
      },
      {
        title: t("accounts.available"),
        span: COLUMN_SPANS[2],
        key: COLUMN_KEYS[2],
        sorter: (a, b) =>
          sortByNumberProperty(a, b, `available.${displayCurrency}`),
      },
    ],
    [i18n.language]
  );

  const { cardsSorters, sortedData } =
    useCardSorters<ProcessedCoinExchangesDataWithKey>({
      CardSorters,
      columns,
      data,
      keys: COLUMN_KEYS,
    });

  const renderContent = ({ item }: { item: ProcessedCoinExchangesData }) => {
    return (
      <CoinAccountCard
        item={item}
        columnSpans={COLUMN_SPANS}
        precisionSelector={precisionSelector}
        displayCurrency={displayCurrency}
      />
    );
  };

  return (
    <FlatList
      data={sortedData || []}
      renderContent={renderContent}
      renderTopContainer={() => {
        return (
          <div style={{ marginBottom: 8 }}>
            {renderTopContainer()}
            {cardsSorters}
          </div>
        );
      }}
      wrapperStyle={WRAPPER_STYLE}
      emptyState={
        <EmptyView
          iconKey={EmptyStatesType.Connect}
          title={t("accounts.no_accounts")}
          iconWidth={100}
        />
      }
    />
  );
};

export default CoinAccountsCards;

const COLUMN_SPANS = [10, 7, 7];
const COLUMN_KEYS = ["account", "total", "available"];
const WRAPPER_STYLE = { paddingBottom: 24 };
